import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import RouteAnnouncer from "../RouteAnnouncer";
import SideMenu from "./SideMenu";
import LandingHeader from "../features/LandingPage/components/LandingHeader";

const LEGAL_PAGE_PATHS = ["/disclaimer", "/privacy-policy", "/terms-of-use"];
import type { ImainMenu } from "../app/models/MainMenuModal";
import { useEffect, useRef, useState } from "react";

import {
  useGetChatUsersQuery,
  useLazyGetMasterMenuQuery,
  useLazyGetUnreadChatMessageCountQuery,
} from "../app/appApiSlice";
import { useAppSelector } from "../app/hooks";
import { selectEmail, selectResourceId, selectRole } from "../app/auth/createAuthSlice";
import { useNotifications } from "../features/Notifications/NotificationContext";
import NotificationDropdown from "../features/Notifications/components/NotificationDropdown";

type RoleView = "employee" | "admin";
const ROLE_VIEW_STORAGE_KEY = "alumniPortal.roleView";
const CONVERSATION_UNREAD_POLL_INTERVAL_MS = 20000;
const CONVERSATION_UNREAD_SYNC_EVENT = "conversation-unread-count-updated";
const DESKTOP_MIN_WIDTH_PX = 1025;

const normalizeThreadId = (threadId?: string) =>
  typeof threadId === "string" ? threadId.trim().toLowerCase() : "";

function AppLayout() {
  const location = useLocation();
  const normalizedPathName = location.pathname.toLowerCase();
  const isConversationPage = normalizedPathName === "/conversations";
  const isConversationRoute =
    normalizedPathName.includes("/conversations") ||
    normalizedPathName.includes("/admin/conversation");
  const navigate = useNavigate();
  const resourceId = useAppSelector(selectResourceId);
  const email = useAppSelector(selectEmail) || "";
  const roleData = useAppSelector(selectRole);
  const mainContentRef = useRef<HTMLElement | null>(null);
  const notificationMenuRef = useRef<HTMLDivElement | null>(null);
  // Sidebar state
  const [collapsed, setCollapsed] = useState(false); 
  const [mobileOpen, setMobileOpen] = useState(false); 
  const [isDesktopMenuSticky, setIsDesktopMenuSticky] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [conversationUnreadCount, setConversationUnreadCount] = useState(0);
  const { unreadCount, previewNotifications, markAllAsRead } = useNotifications();
  // useEffect(() => {
  //   if (window?._satellite) {
  //     window._satellite.track("page_load");
  //   }
  // }, [location]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname.split("/").filter(Boolean);
    document.title = path.length
      ? `${path.toString().charAt(0).toUpperCase()}${path.toString().slice(1).toLowerCase()} - Alumni Portal`
      : "Alumni Portal";
  }, [location]);   
  const toggleSidebar = () => setCollapsed((prev) => !prev);
   
  const toggleMobileSidebar = () => setMobileOpen((prev) => !prev);

  const roleNames = Array.isArray(roleData)
    ? roleData
      .map((role) => String(role?.roleName ?? "").trim())
      .filter(Boolean)
    : [String(roleData ?? "").trim()].filter(Boolean);
  const uniqueRoleNames = Array.from(new Set(roleNames.map((role) => role.toLowerCase())));

  const hasAdminRole =
    uniqueRoleNames.some((role) => role.includes("admin"));
  const hasEmployeeRole =
    uniqueRoleNames.some((role) => role.includes("employee"));
  const firstOtherRole = roleNames.find((roleName) => {
    const normalizedRole = roleName.toLowerCase();
    return !normalizedRole.includes("admin") && !normalizedRole.includes("employee");
  });

  const canSwitchRoleView = hasAdminRole;
  const defaultRoleView: RoleView = "employee";
  const [activeRoleView, setActiveRoleView] = useState<RoleView>(() => {
    if (typeof window === "undefined") {
      return defaultRoleView;
    }

    const storedRoleView = sessionStorage.getItem(ROLE_VIEW_STORAGE_KEY);
    if (canSwitchRoleView && (storedRoleView === "admin" || storedRoleView === "employee")) {
      return storedRoleView;
    }

    return defaultRoleView;
  });

  useEffect(() => {
    if (canSwitchRoleView && typeof window !== "undefined") {
      const storedRoleView = sessionStorage.getItem(ROLE_VIEW_STORAGE_KEY);
      if (storedRoleView === "admin" || storedRoleView === "employee") {
        setActiveRoleView(storedRoleView);
        return;
      }
    }

    setActiveRoleView(defaultRoleView);
  }, [defaultRoleView, canSwitchRoleView]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (canSwitchRoleView) {
      sessionStorage.setItem(ROLE_VIEW_STORAGE_KEY, activeRoleView);
    } else {
      sessionStorage.removeItem(ROLE_VIEW_STORAGE_KEY);
    }
  }, [activeRoleView, canSwitchRoleView]);

  const [menuItems, setMenuItems] = useState<ImainMenu[]>([]);

  const [getMenuData, { data }] = useLazyGetMasterMenuQuery();
  const [loadUnreadCount] = useLazyGetUnreadChatMessageCountQuery();

  const {
    data: chatConversations = [],
  } = useGetChatUsersQuery(undefined, {
    skip: !email || isConversationRoute,
    pollingInterval: CONVERSATION_UNREAD_POLL_INTERVAL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const selectedMenuRole = hasAdminRole
    ? activeRoleView === "admin"
      ? "Admin"
      : "Employee"
    : hasEmployeeRole
      ? "Employee"
      : firstOtherRole || "Employee";

  const handleRoleChange = (nextRole: RoleView) => {
    setActiveRoleView(nextRole);

    if (nextRole === "admin") {
      if (location.pathname !== "/dashboard") {
        navigate("/dashboard");
      }
      return;
    }

    if (location.pathname !== "/home") {
      navigate("/home");
    }
  };

  useEffect(() => {
    if (resourceId) {
      getMenuData({ userId: resourceId, userRole: selectedMenuRole });
    }
  }, [resourceId, selectedMenuRole, getMenuData]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setIsNotificationOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!isNotificationOpen) {
        return;
      }

      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(event.target as Node)
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isNotificationOpen]);

  useEffect(() => {
    const handleWindowScroll = () => {
      const isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH_PX;
      const mainScrollTop = mainContentRef.current?.scrollTop ?? 0;
      setIsDesktopMenuSticky(isDesktop && (window.scrollY > 0 || mainScrollTop > 0));
    };

    handleWindowScroll();
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    window.addEventListener("resize", handleWindowScroll);

    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
      window.removeEventListener("resize", handleWindowScroll);
    };
  }, []);

  const handleMainScroll = (event: any) => {
    const isDesktop = window.innerWidth >= DESKTOP_MIN_WIDTH_PX;
    setIsDesktopMenuSticky(isDesktop && event.currentTarget.scrollTop > 0);
  };

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setMenuItems(data as ImainMenu[]);
    } else {
      setMenuItems([]);
    }
  }, [data]);

  useEffect(() => {
    if (isConversationRoute) {
      return;
    }

    if (!email || !Array.isArray(chatConversations) || chatConversations.length === 0) {
      setConversationUnreadCount(0);
      return;
    }

    let cancelled = false;

    const fetchUnreadCount = async () => {
      const threadIds = Array.from(
        new Set(
          chatConversations
            .map((conversation: any) => normalizeThreadId(conversation?.threadId))
            .filter(Boolean)
        )
      );

      if (!threadIds.length) {
        if (!cancelled) {
          setConversationUnreadCount(0);
        }
        return;
      }

      const unreadValues = await Promise.all(
        threadIds.map(async (threadId) => {
          try {
            const response = await loadUnreadCount(
              {
                threadId,
                userId: email,
              },
              false
            ).unwrap();

            return typeof response?.unreadCount === "number" ? Math.max(0, response.unreadCount) : 0;
          } catch {
            return 0;
          }
        })
      );

      if (cancelled) {
        return;
      }

      const totalUnread = unreadValues.reduce((sum, value) => sum + value, 0);
      setConversationUnreadCount(totalUnread);
    };

    void fetchUnreadCount();

    const interval = window.setInterval(() => {
      void fetchUnreadCount();
    }, CONVERSATION_UNREAD_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [chatConversations, email, isConversationRoute, loadUnreadCount]);

  useEffect(() => {
    const handleConversationUnreadSync = (event: Event) => {
      const customEvent = event as CustomEvent<{ totalUnreadCount?: number }>;
      const nextUnreadCount = Number(customEvent?.detail?.totalUnreadCount);

      if (!Number.isFinite(nextUnreadCount)) {
        return;
      }

      setConversationUnreadCount(Math.max(0, nextUnreadCount));
    };

    window.addEventListener(CONVERSATION_UNREAD_SYNC_EVENT, handleConversationUnreadSync);

    return () => {
      window.removeEventListener(CONVERSATION_UNREAD_SYNC_EVENT, handleConversationUnreadSync);
    };
  }, []);

  const searchParams = new URLSearchParams(location.search);
  const isLegalFromLanding =
    LEGAL_PAGE_PATHS.includes(location.pathname) &&
    (!!(location.state as { fromLanding?: boolean } | null)?.fromLanding ||
      searchParams.get("fromLanding") === "true");

  return (
    <>
    {isLegalFromLanding ? (
      <div className="flex flex-col min-h-screen">
        <RouteAnnouncer />
        <LandingHeader />
        <main className="flex-1 pt-14">
          <Outlet />
        </main>
        <Footer fromLanding />
      </div>
    ) : (
    <div className="flex">
      <RouteAnnouncer />
      <Header
        title="Alumni"
        notificationCount={unreadCount}
        showRoleToggle={canSwitchRoleView}
        activeRoleView={activeRoleView}
        onRoleChange={handleRoleChange}
        notificationContainerRef={notificationMenuRef}
        notificationDropdown={
          isNotificationOpen ? (
            <NotificationDropdown
              items={previewNotifications}
              onViewAllClick={() => setIsNotificationOpen(false)}
            />
          ) : null
        }
        onMenuClick={() => {
          if (window.innerWidth < DESKTOP_MIN_WIDTH_PX) {
            toggleMobileSidebar();
          } else {
            toggleSidebar();
          }
        }}
        onNotificationClick={() => {
          const opening = !isNotificationOpen;
          setIsNotificationOpen(opening);
          if (opening) {
            markAllAsRead();
          }
        }}
      />
      {/* Sidebar */}
      <SideMenu
        items={menuItems}
        conversationUnreadCount={conversationUnreadCount}
        className={`
          z-50 pt-14 bg-black text-white overflow-visible transition-w duration-300
          min-[1025px]:transition-[transform,opacity,box-shadow] min-[1025px]:duration-300 min-[1025px]:ease-out
          ${isDesktopMenuSticky
            ? "min-[1025px]:sticky min-[1025px]:top-0 min-[1025px]:h-screen min-[1025px]:translate-y-0 min-[1025px]:opacity-100 min-[1025px]:shadow-lg"
            : "min-[1025px]:translate-y-0 min-[1025px]:opacity-95"}
          ${collapsed ? "w-24" : "w-64"} 
          hidden min-[1025px]:block
        `}
        collapsed={collapsed} 
      />
      {mobileOpen && (
        <div className="fixed inset-0 z-50 min-[1025px]:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 min-[1025px]:top-0 top-10 bottom-0 w-64 md:h-full bg-black text-white p-4 overflow-visible  overflow-y-auto  h-[calc(100% - 2.5rem)]">
            <SideMenu
              items={menuItems}
              conversationUnreadCount={conversationUnreadCount}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        ref={mainContentRef}
        onScroll={handleMainScroll}
        className={`
          flex-1 flex overflow-x-hidden overflow-y-auto flex-col pt-14 transition-all duration-300
          ${collapsed ? "" : ""}
        `}
      >
        <div
          className={`w-full min-h-[58vh]! p-4 ${isConversationPage ? "max-w-full" : "xl:max-w-341.75 mx-auto"}`}
        >
          <div className="">
            <Outlet />
          </div>          
        </div>
        <Footer />
      </main>       
    </div>
    )}    
    </>
  );
}

export default AppLayout;